import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import Pager from "@/components/Pager";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Search, ChevronDown, Wifi, WifiOff, Pencil, Trash2, Plus, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KES, formatRelative, formatCountdown } from "@/lib/format";
import { formatMacInput } from "@/lib/mac";
import { toast } from "sonner";

const PAGE_SIZE = 20;

export default function Customers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [devices, setDevices] = useState({});
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [editing, setEditing] = useState(null); // { customerId, deviceId }
  const [editName, setEditName] = useState("");
  const [editMac, setEditMac] = useState("");
  const [editError, setEditError] = useState("");
  const [addingFor, setAddingFor] = useState(null); // customerId
  const [addMac, setAddMac] = useState("");
  const [addName, setAddName] = useState("");
  const [addError, setAddError] = useState("");
  const [busyDeviceId, setBusyDeviceId] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  const load = async (q, p) => {
    try {
      const { data } = await api.get("/admin/customers", {
        params: { ...(q ? { search: q } : {}), page: p, page_size: PAGE_SIZE },
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);
  useEffect(() => { load(debouncedSearch, page); }, [debouncedSearch, page]);

  const reloadDevices = async (id) => {
    const { data } = await api.get(`/admin/customers/${id}/devices`);
    setDevices((cur) => ({ ...cur, [id]: data }));
  };

  const toggleExpand = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!devices[id]) {
      setLoadingDevices(true);
      try {
        await reloadDevices(id);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoadingDevices(false);
      }
    }
  };

  const openEdit = (customerId, d) => {
    setEditing({ customerId, deviceId: d.id });
    setEditName(d.device_name || "");
    setEditMac(d.mac_address);
    setEditError("");
  };

  const saveEdit = async () => {
    const { customerId, deviceId } = editing;
    const device = (devices[customerId] || []).find((d) => d.id === deviceId);
    const body = { device_name: editName };
    if (!device.is_connected) body.mac_address = editMac;
    setEditError("");
    try {
      await api.patch(`/admin/customers/${customerId}/devices/${deviceId}`, body);
      setEditing(null);
      await reloadDevices(customerId);
      toast.success("Device updated");
    } catch (e) {
      setEditError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const removeDevice = async (customerId, d) => {
    if (!window.confirm(`Remove "${d.device_name || d.mac_address}"?`)) return;
    try {
      await api.delete(`/admin/customers/${customerId}/devices/${d.id}`);
      await reloadDevices(customerId);
      toast.success("Device removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const disconnectDevice = async (customerId, d) => {
    setBusyDeviceId(d.id);
    try {
      await api.post(`/admin/customers/${customerId}/devices/${d.id}/disconnect`);
      await reloadDevices(customerId);
      toast.success("Device disconnected");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyDeviceId(null);
    }
  };

  const connectDevice = async (customerId, d) => {
    setBusyDeviceId(d.id);
    try {
      await api.post(`/admin/customers/${customerId}/devices/${d.id}/connect`);
      await reloadDevices(customerId);
      toast.success("Device connected");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyDeviceId(null);
    }
  };

  const openAdd = (customerId) => {
    setAddingFor(customerId);
    setAddMac(""); setAddName(""); setAddError("");
  };

  const saveAdd = async () => {
    if (!addMac.trim()) { setAddError("Enter a MAC address"); return; }
    setAddError("");
    try {
      await api.post(`/admin/customers/${addingFor}/devices`, { mac_address: addMac, device_name: addName });
      await reloadDevices(addingFor);
      setAddingFor(null);
      toast.success("Device registered");
    } catch (e) {
      setAddError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div>
      <PageHeader
        testId="customers-header"
        title="Customers"
        subtitle="All hotspot users who have paid at least once"
      />
      <div className="p-4 md:p-8">
        <div className="mb-4 relative max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-nhp-muted" />
          <Input
            data-testid="customer-search"
            placeholder="Search by phone or MAC address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-nhp-surface border-nhp-border text-nhp-text rounded-none h-10 font-mono"
          />
        </div>

        <div className="border border-nhp-border bg-nhp-surface">
          <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
            <div className="col-span-3">Phone</div>
            <div className="col-span-2">Devices</div>
            <div className="col-span-2">Total Spent</div>
            <div className="col-span-2">Subscriptions</div>
            <div className="col-span-2">Last Seen</div>
            <div className="col-span-1">Active</div>
          </div>
          <div className="divide-y divide-nhp-border">
            {items.length === 0 && <div className="p-8 text-center text-sm text-nhp-muted">No customers found.</div>}
            {items.map((c) => (
              <div key={c.id}>
                <button
                  type="button"
                  data-testid={`customer-row-${c.id}`}
                  onClick={() => toggleExpand(c.id)}
                  className="w-full flex flex-col gap-2 md:grid md:grid-cols-12 px-4 md:px-6 py-4 md:items-center hover:bg-nhp-bg text-left"
                >
                  <div className="flex items-center justify-between md:contents">
                    <div className="md:col-span-3 font-mono flex items-center gap-2">
                      <ChevronDown className={`h-3.5 w-3.5 text-nhp-muted transition-transform shrink-0 ${expanded === c.id ? "rotate-180" : ""}`} />
                      {c.phone}
                    </div>
                    <div className="md:col-span-1 md:order-last">
                      {c.active_subscription ? (
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border text-nhp-primary border-nhp-primary/40 bg-nhp-primary/10">ON</span>
                      ) : (
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border text-nhp-muted border-nhp-border">-</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 md:contents text-sm">
                    <div className="md:col-span-2 font-mono tabular-nums">
                      {c.connected_devices > 0 ? (
                        <span className="text-nhp-primary flex items-center gap-1.5"><Wifi className="h-3.5 w-3.5" />{c.connected_devices} online</span>
                      ) : (
                        <span className="text-nhp-muted"><span className="md:hidden">Devices: </span>-</span>
                      )}
                    </div>
                    <div className="md:col-span-2 font-mono font-bold text-nhp-primary tabular-nums">{KES(c.total_spent_kes)}</div>
                    <div className="md:col-span-2 font-mono"><span className="md:hidden text-nhp-muted">Subs: </span>{c.subscription_count}</div>
                    <div className="md:col-span-2 text-nhp-muted">{formatRelative(c.last_seen)}</div>
                  </div>
                </button>

                {expanded === c.id && (
                  <div className="px-4 md:px-6 pb-4 bg-nhp-bg" data-testid={`customer-devices-${c.id}`}>
                    <div className="flex justify-end pt-2">
                      <button
                        type="button"
                        data-testid={`admin-device-add-${c.id}`}
                        onClick={(e) => { e.stopPropagation(); openAdd(c.id); }}
                        className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-nhp-primary hover:underline"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add device
                      </button>
                    </div>
                    {loadingDevices && !devices[c.id] ? (
                      <div className="py-4 text-sm text-nhp-muted">Loading devices...</div>
                    ) : (devices[c.id] || []).length === 0 ? (
                      <div className="py-4 text-sm text-nhp-muted">No devices registered.</div>
                    ) : (
                      <div className="divide-y divide-nhp-border border border-nhp-border bg-nhp-surface mt-2">
                        {devices[c.id].map((d) => (
                          <div key={d.id} className="px-4 py-3 flex items-center justify-between text-sm">
                            <div className="flex items-center gap-3">
                              {d.is_connected ? <Wifi className="h-4 w-4 text-nhp-primary" /> : <WifiOff className="h-4 w-4 text-nhp-muted" />}
                              <div>
                                <div className="font-medium">{d.device_name || "Unnamed device"}</div>
                                <div className="font-mono text-xs text-nhp-muted">{d.mac_address}</div>
                                {d.expires_at && (
                                  <div className="font-mono text-[11px] text-nhp-muted mt-0.5 tabular-nums">
                                    {new Date(d.expires_at).getTime() - now > 0
                                      ? `${formatCountdown(new Date(d.expires_at).getTime() - now)} left`
                                      : "Time expired"}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right font-mono text-xs text-nhp-muted">
                                {d.is_connected ? <span className="text-nhp-primary">{d.ip_address}</span> : "not connected"}
                              </div>
                              {d.is_connected ? (
                                <button
                                  type="button"
                                  data-testid={`admin-device-disconnect-${d.id}`}
                                  onClick={(e) => { e.stopPropagation(); disconnectDevice(c.id, d); }}
                                  disabled={busyDeviceId === d.id}
                                  className="h-7 px-2.5 grid place-items-center border border-nhp-border text-[10px] font-mono uppercase tracking-[0.15em] text-nhp-muted hover:text-nhp-text hover:bg-nhp-surface disabled:opacity-40"
                                >
                                  {busyDeviceId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Disconnect"}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  data-testid={`admin-device-connect-${d.id}`}
                                  onClick={(e) => { e.stopPropagation(); connectDevice(c.id, d); }}
                                  disabled={busyDeviceId === d.id}
                                  className="h-7 px-2.5 grid place-items-center border border-nhp-primary/40 text-[10px] font-mono uppercase tracking-[0.15em] text-nhp-primary hover:bg-nhp-primary/10 disabled:opacity-40"
                                >
                                  {busyDeviceId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Connect"}
                                </button>
                              )}
                              <button
                                type="button"
                                data-testid={`admin-device-edit-${d.id}`}
                                onClick={(e) => { e.stopPropagation(); openEdit(c.id, d); }}
                                className="h-7 w-7 grid place-items-center text-nhp-muted hover:text-nhp-text"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                data-testid={`admin-device-remove-${d.id}`}
                                onClick={(e) => { e.stopPropagation(); removeDevice(c.id, d); }}
                                disabled={d.is_connected}
                                title={d.is_connected ? "Disconnect this device before removing it" : "Remove device"}
                                className="h-7 w-7 grid place-items-center text-nhp-muted hover:text-red-500 disabled:opacity-30 disabled:hover:text-nhp-muted"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <Pager page={page} total={total} pageSize={PAGE_SIZE} itemLabel="customers" onChange={setPage} testId="customers" />
        </div>
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-nhp-surface border-nhp-border rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle>Edit device</DialogTitle>
            <DialogDescription>Update this customer's device name or MAC address.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Device name</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                     className="mt-1 h-11 rounded-none bg-nhp-bg border-nhp-border text-nhp-text" placeholder="Home phone" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">MAC Address</Label>
              <Input value={editMac} onChange={(e) => { setEditMac(formatMacInput(e.target.value)); setEditError(""); }}
                     disabled={editing && (devices[editing.customerId] || []).find((d) => d.id === editing.deviceId)?.is_connected}
                     type="text" inputMode="text" autoComplete="off" autoCapitalize="characters"
                     className="mt-1 h-11 rounded-none font-mono bg-nhp-bg border-nhp-border text-nhp-text disabled:opacity-50" placeholder="AA:BB:CC:11:22:33" />
              {editing && (devices[editing.customerId] || []).find((d) => d.id === editing.deviceId)?.is_connected && (
                <p className="mt-1 text-xs text-nhp-muted">Disconnect this device first to change its MAC address.</p>
              )}
            </div>
            {editError && (
              <div className="border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm text-red-500">{editError}</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} className="rounded-none">Cancel</Button>
            <Button onClick={saveEdit} className="rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">
              SAVE
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addingFor !== null} onOpenChange={(o) => !o && setAddingFor(null)}>
        <DialogContent className="bg-nhp-surface border-nhp-border rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle>Register a device</DialogTitle>
            <DialogDescription>
              For devices that can't reach the captive portal themselves (e.g. a smart TV) — find
              the MAC in the device's own network settings, or its DHCP lease on the router.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">MAC Address</Label>
              <Input value={addMac} onChange={(e) => { setAddMac(formatMacInput(e.target.value)); setAddError(""); }}
                     type="text" inputMode="text" autoComplete="off" autoCapitalize="characters"
                     className="mt-1 h-11 rounded-none font-mono bg-nhp-bg border-nhp-border text-nhp-text" placeholder="AA:BB:CC:11:22:33" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Device name (optional)</Label>
              <Input value={addName} onChange={(e) => setAddName(e.target.value)}
                     className="mt-1 h-11 rounded-none bg-nhp-bg border-nhp-border text-nhp-text" placeholder="Living room TV" />
            </div>
            {addError && (
              <div className="border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm text-red-500">{addError}</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddingFor(null)} className="rounded-none">Cancel</Button>
            <Button onClick={saveAdd} className="rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">
              REGISTER
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
